import { useState } from 'react';
import { LoadFailed } from '../components/QueryState';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MIN_SUPPORT_REASON_LENGTH,
  SUPPORT_GRANT_MAX_MS,
  canAdministerPlatform,
  canReissueSetupLink,
  supportGrantIssues,
} from '@promanage/core';
import { Button, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { SettingsNav } from '../features/settings/SettingsNav';
import { enterOrganisation, useSession } from '../features/auth/session';
import { timeUntil } from '../features/users/format';
import { trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/settings/organisations/')({
  component: OrganisationsPage,
});

type Organisation = RouterOutputs['platform']['listOrganisations'][number];

/**
 * Every organisation on ProManage, and the door into one.
 *
 * This page is the whole of our cross-customer surface, and it is deliberately thin: names,
 * member counts, and a button that asks for a REASON. Nothing on this screen reads a single
 * row of anybody's data.
 *
 * Taking access does not bypass tenant isolation — it creates an ordinary, expiring
 * membership in the target organisation. Afterwards you switch into it and get a normal
 * session with a normal `org_id`, subject to exactly the same row-level security as the
 * agency's own staff, and still unable to post a journal. The grant is written to THEIR audit
 * log with the reason and the expiry, so the customer can see it. See docs/SECURITY.md §6.
 *
 * Setting one UP is the same story from the other end — see /settings/organisations/new. It
 * hands us no membership at all, so an agency we opened this morning is already one we have to
 * ask to get into. The only extra thing a row here offers is re-sending the first sign-in link,
 * and only until that agency has signed in once.
 */
function OrganisationsPage() {
  const orgs = useQuery(
    trpc.platform.listOrganisations.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load organisations' },
      staleTime: 15_000,
    }),
  );
  const session = useSession();
  // Support staff see this list and may ask for access; opening a customer is the narrower
  // `platform_admin` role, and the server refuses it regardless of what is drawn here.
  const isAdmin = canAdministerPlatform(session?.roles ?? []);
  const now = new Date();

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Organisations"
        subtitle={orgs.data ? `${orgs.data.length} on ProManage` : 'Platform administration'}
        actions={
          isAdmin ? (
            <Link to="/settings/organisations/new">
              <Button size="sm">
                <span aria-hidden="true">+</span> Add agency
              </Button>
            </Link>
          ) : undefined
        }
      />

      <SettingsNav />

      <Panel className="mb-4 border-l-4 border-l-warn p-5">
        <p className="text-[0.8125rem] leading-relaxed">
          <span className="font-bold">Access here is visible to the customer.</span> Every grant is
          written to that agency&rsquo;s own audit log with the reason you give and the time it
          expires. It ends by itself; end it sooner when you are finished.
        </p>
      </Panel>

      {orgs.isError ? (
        <Panel className="p-6">
          <LoadFailed inline retry={() => void orgs.refetch()} retrying={orgs.isFetching} />
        </Panel>
      ) : null}

      {orgs.isPending ? (
        <Panel className="p-6">
          <p className="text-[0.875rem] text-ink-muted">Loading…</p>
        </Panel>
      ) : null}

      {orgs.data ? (
        <Panel>
          <PanelHeader title="Agencies" subtitle="Names and member counts only." />
          <ul className="border-t border-rule">
            {orgs.data.map((org) => (
              <OrganisationRow key={org.id} org={org} now={now} isAdmin={isAdmin} />
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}

function OrganisationRow({
  org,
  now,
  isAdmin,
}: {
  org: Organisation;
  now: Date;
  isAdmin: boolean;
}) {
  const client = useQueryClient();
  const [asking, setAsking] = useState(false);

  const remaining = timeUntil(org.supportExpiresAt, now);

  /*
   * The door that shuts on its own — `canReissueSetupLink` in core, asked here so the button is
   * not offered and then refused. The rule that binds is in `platform_reissue_setup_token`.
   */
  const canReissue =
    isAdmin &&
    org.kind === 'agency' &&
    canReissueSetupLink({
      hasLoggedIn: org.hasLoggedIn,
      firstUserAwaitingSetup: org.awaitingSetup,
    });

  const reissue = useMutation(
    trpc.platform.reissueSetup.mutationOptions({
      meta: { errorTitle: 'Could not reissue the setup link' },
    }),
  );
  const refresh = () => {
    void client.invalidateQueries({ queryKey: trpc.platform.listOrganisations.queryKey() });
    void client.invalidateQueries({ queryKey: trpc.auth.organisations.queryKey() });
  };

  const endAccess = useMutation(
    trpc.platform.endAccess.mutationOptions({
      meta: { errorTitle: 'Could not end support access' },
      onSuccess: refresh,
    }),
  );

  const switchOrg = useMutation(
    trpc.auth.switchOrg.mutationOptions({
      meta: { errorTitle: 'Could not switch organisation' },
      // A new session in someone else's organisation. Nothing of ours may survive the switch,
      // so the page starts again in theirs — see `enterOrganisation`.
      onSuccess: enterOrganisation,
    }),
  );

  return (
    <li className="border-t border-rule first:border-t-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5">
        <div className="min-w-0 flex-1 basis-56">
          <p className="truncate text-[0.875rem] font-semibold">{org.name}</p>
          <p className="text-[0.75rem] text-ink-muted tabular-nums">
            {org.memberCount} {org.memberCount === 1 ? 'member' : 'members'}
            {/* What it is storing — there is no ceiling, so this is where growth is seen. */}
            {' · '}
            {org.storage.files === 0
              ? 'nothing stored'
              : `${org.storage.size} in ${org.storage.files.toLocaleString('en-NZ')} ${
                  org.storage.files === 1 ? 'file' : 'files'
                }`}
          </p>
        </div>

        <div className="flex basis-44 flex-wrap items-center gap-1.5">
          {org.kind === 'platform' ? <Chip tone="accent">ProManage</Chip> : null}
          {org.status !== 'active' ? <Chip tone="neutral">{org.status}</Chip> : null}
          {remaining ? <Chip tone="warn">Access · {remaining} left</Chip> : null}
          {org.kind === 'agency' && !org.hasLoggedIn ? (
            <Chip tone="neutral">Never signed in</Chip>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap gap-1.5">
          {org.kind === 'platform' ? (
            <span className="text-[0.75rem] text-ink-subtle">This is us</span>
          ) : remaining ? (
            <>
              <Button
                size="sm"
                disabled={switchOrg.isPending}
                onClick={() => switchOrg.mutate({ orgId: org.id })}
              >
                Switch into
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={endAccess.isPending}
                onClick={() => endAccess.mutate({ orgId: org.id })}
              >
                End access
              </Button>
            </>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => setAsking((v) => !v)}>
              {asking ? 'Cancel' : 'Request access'}
            </Button>
          )}

          {canReissue ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={reissue.isPending}
              onClick={() => reissue.mutate({ orgId: org.id })}
            >
              {reissue.isPending ? 'Re-issuing' : 'New sign-in link'}
            </Button>
          ) : null}
        </div>
      </div>

      {reissue.data ? (
        <div className="border-t border-rule bg-surface-sunk px-5 py-4">
          <p className="text-[0.8125rem]">
            A fresh link for <span className="font-semibold">{reissue.data.email}</span>. It works
            once, expires in seven days, and cannot be shown again.
          </p>
          <p className="mt-2 rounded-md bg-surface p-3 font-mono text-[0.75rem] break-all select-all">
            {`${window.location.origin}${reissue.data.setupPath}`}
          </p>
        </div>
      ) : null}

      {asking ? (
        <GrantForm
          orgId={org.id}
          orgName={org.name}
          onGranted={() => {
            setAsking(false);
            refresh();
          }}
        />
      ) : null}
    </li>
  );
}

/** Sensible windows, all inside the 8-hour ceiling the database enforces. */
const DURATIONS = [
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 240, label: '4 hours' },
  { minutes: SUPPORT_GRANT_MAX_MS / 60_000, label: '8 hours (maximum)' },
];

function GrantForm({
  orgId,
  orgName,
  onGranted,
}: {
  orgId: string;
  orgName: string;
  onGranted: () => void;
}) {
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState(120);
  const [issue, setIssue] = useState<string | undefined>();

  const grant = useMutation(
    trpc.platform.grantAccess.mutationOptions({
      meta: { errorTitle: 'Could not take access' },
      onSuccess: onGranted,
    }),
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    // The same rule the API and the database both apply, run here for a faster answer —
    // not instead of them.
    const issues = supportGrantIssues(reason, minutes * 60_000);
    if (issues.length > 0) {
      setIssue(issues[0]);
      return;
    }

    setIssue(undefined);
    grant.mutate({ orgId, reason: reason.trim(), durationMinutes: minutes });
  };

  return (
    <form onSubmit={submit} className="border-t border-rule bg-surface-sunk px-5 py-4" noValidate>
      <div className="grid gap-4 md:grid-cols-[1fr_12rem]">
        <Field
          label="Why do you need access?"
          required
          error={issue}
          hint={`At least ${MIN_SUPPORT_REASON_LENGTH} characters. ${orgName} will read this in their audit log.`}
        >
          {(props) => (
            <Input
              {...props}
              autoFocus
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setIssue(undefined);
              }}
              placeholder="Ticket 412 — reconciliation shows a duplicate receipt"
            />
          )}
        </Field>

        <Field label="For how long" required>
          {(props) => (
            <Select
              {...props}
              value={String(minutes)}
              onChange={(e) => setMinutes(Number(e.target.value))}
            >
              {DURATIONS.map((option) => (
                <option key={option.minutes} value={option.minutes}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <Button type="submit" size="sm" className="mt-3" disabled={grant.isPending}>
        {grant.isPending ? 'Taking access' : 'Take access'}
      </Button>
    </form>
  );
}
